import classNames from "classnames";
import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ProductCompareTheme } from "../../../constants";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { IAttributeOptionGroup } from "../../../models/product-compare";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { useShouldShowPricing } from "../../../utils/settings";
import { updatePriceSelectValue } from "../reducers";
import { SizeSelector as SizeSelectorComponent } from "./ProductCompareSizeSelectorContent";

import styles from "./index.module.scss";

interface IProps {
    sizeAttributeOptionGroup: IAttributeOptionGroup;
    preselectedSize: string | null;
    showSizeSelector?: boolean | null;
    theme?: ProductCompareTheme | null;
}

const getStoredSize = (): string | null => {
    const window = getWindow();
    return window?.localStorage
        ? window.localStorage.getItem("selectedSize")
        : null;
};

const setStoredSize = (value: string): void => {
    const window = getWindow();
    if (window?.localStorage) {
        window.localStorage.setItem("selectedSize", value);
    }
};

export const SizeSelector = (props: IProps): React.ReactNode => {
    const shouldShowPricing = useShouldShowPricing();
    const dispatch = useAppDispatch();
    const priceSelectValue = useAppSelector(
        (rootState) => rootState.productcompare.priceSelectValue,
    );

    useMountEffect(() => {
        if (props.preselectedSize) {
            dispatch(updatePriceSelectValue({ value: props.preselectedSize }));
        } else {
            const selectedSize = getStoredSize();
            if (selectedSize) {
                dispatch(updatePriceSelectValue({ value: selectedSize }));
            }
        }
    });

    const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = e.currentTarget.value;
        dispatch(updatePriceSelectValue({ value }));
        setStoredSize(value);
    };

    if (!shouldShowPricing || !props.showSizeSelector) {
        return null;
    }

    const rootClassNames = classNames({
        [styles.root]: true,
        [props.theme || ""]: props.theme === ProductCompareTheme.THEME_ONE,
    });

    return (
        <div className={rootClassNames}>
            <SizeSelectorComponent
                sizeAttributeOptionGroupName={
                    props.sizeAttributeOptionGroup.name
                }
                sizeAttributeOptions={props.sizeAttributeOptionGroup.options}
                priceSelectValue={priceSelectValue}
                onSelectChange={onSelectChange}
            />
        </div>
    );
};
